import React from 'react';
import { Box, Chip, TextField, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TextFieldProps } from '@mui/material/TextField';

/**
 * Shared presentational field primitives for the Complex Services stage forms.
 *
 * These fix three cross-cutting form issues that every Complex Services stage
 * hit independently:
 *
 *  1. The "Auto-filled" / "User Edited" badge used to be placed loosely (often
 *     absolutely) which made it collide with an outlined TextField's floating
 *     label (e.g. "Base Year Value") and shift the field when it appeared or
 *     disappeared. `BadgeSlot` reserves a FIXED vertical band ABOVE every field
 *     so the field never moves and the badge never overlaps the label.
 *  2. A field can be marked REQUIRED (asterisk + red error state when empty).
 *  3. A field can be intrinsically NON-EDITABLE (derived/read-only): greyed out
 *     (MUI `disabled`) with a clear reason shown as persistent helper text and a
 *     tooltip explaining why.
 *
 * Everything here is presentational and stateless — no stage-specific imports,
 * no data fetching. Stages own the state and pass values/badges down.
 */

/** The badge shown above a field. `null`/`undefined` reserves the slot but
 * renders no chip (so rows stay vertically aligned). */
export type FieldBadgeKind = 'auto-filled' | 'user-edited' | null | undefined;

/** Fixed height (px) of the reserved badge band above every field. Also used as
 * the chip height so the chip fills the slot exactly. */
export const BADGE_SLOT_HEIGHT = 22;

/**
 * A fixed-height band rendered ABOVE a field that holds the auto-filled /
 * user-edited badge. The Box ALWAYS renders at `BADGE_SLOT_HEIGHT` even when
 * there is no badge, so a badge appearing or disappearing never shifts the
 * field below it and never overlaps the outlined TextField's floating label.
 */
export function BadgeSlot({ badge }: { badge?: FieldBadgeKind }): React.ReactElement {
  return (
    <Box
      data-testid="cs-badge-slot"
      sx={{ height: BADGE_SLOT_HEIGHT, mb: 1.5, display: 'flex', alignItems: 'center' }}
    >
      {badge === 'auto-filled' && (
        <Chip
          label="Auto-filled"
          size="small"
          color="success"
          variant="outlined"
          data-testid="cs-badge-auto"
          sx={{ fontSize: '0.7rem', height: BADGE_SLOT_HEIGHT }}
        />
      )}
      {badge === 'user-edited' && (
        <Chip
          label="User Edited"
          size="small"
          variant="outlined"
          data-testid="cs-badge-edited"
          sx={{ fontSize: '0.7rem', height: BADGE_SLOT_HEIGHT }}
        />
      )}
    </Box>
  );
}

export interface ComplexFieldProps {
  /** Floating label for the TextField. */
  label: string;
  /** Controlled string value. */
  value: string;
  /** Called with the new string value. Ignored when the field is view-only or
   * intrinsically non-editable. */
  onChange?: (value: string) => void;
  /** Badge shown in the reserved slot above the field. When omitted the slot is
   * still rendered (empty) so rows stay aligned. */
  badge?: FieldBadgeKind;
  /** Marks the field required: shows the asterisk and turns the field red
   * (error) when its trimmed value is empty. Suppressed in view-only /
   * non-editable modes. */
  required?: boolean;
  /** Message shown in the empty-required error state (defaults to 'Required'). */
  errorText?: string;
  /** Global view-only mode: the input is read-only (not greyed/disabled) and no
   * required-error is shown. */
  viewOnly?: boolean;
  /** Intrinsically non-editable derived field: the input is disabled (greyed)
   * and `nonEditableReason` is shown as persistent helper text + tooltip. */
  nonEditable?: boolean;
  /** Reason explaining why the field is non-editable (helper text + tooltip). */
  nonEditableReason?: string;
  /** Optional input type, e.g. 'date'. */
  type?: string;
  multiline?: boolean;
  minRows?: number;
  placeholder?: string;
  size?: TextFieldProps['size'];
  fullWidth?: boolean;
  /** Helper text used when not overridden by the required-error message or a
   * non-editable reason. */
  helperText?: string;
  InputLabelProps?: TextFieldProps['InputLabelProps'];
  /** Passthrough to the underlying `<input>` (e.g. `{ 'data-testid': 'x' }`). */
  inputProps?: TextFieldProps['inputProps'];
  sx?: SxProps<Theme>;
}

/**
 * A complete Complex Services field: a reserved badge slot stacked above a MUI
 * `<TextField>`. Controlled and stateless. Handles the required-error, view-only
 * read-only, and non-editable (disabled + reason) modes described on
 * `ComplexFieldProps`.
 */
export function ComplexField(props: ComplexFieldProps): React.ReactElement {
  const {
    label,
    value,
    onChange,
    badge,
    required,
    errorText,
    viewOnly,
    nonEditable,
    nonEditableReason,
    type,
    multiline,
    minRows,
    placeholder,
    size = 'small',
    fullWidth = true,
    helperText,
    InputLabelProps,
    inputProps,
    sx,
  } = props;

  const showRequiredError = !!required && !viewOnly && !nonEditable && !String(value ?? '').trim();

  // Helper text precedence: a non-editable reason is always shown; otherwise the
  // required-error message; otherwise the caller-supplied helper text.
  let resolvedHelperText: string | undefined;
  if (nonEditable && nonEditableReason) {
    resolvedHelperText = nonEditableReason;
  } else if (showRequiredError) {
    resolvedHelperText = errorText ?? 'Required';
  } else {
    resolvedHelperText = helperText;
  }

  const field = (
    <TextField
      label={label}
      value={value}
      onChange={(e) => {
        if (viewOnly || nonEditable) return;
        onChange?.(e.target.value);
      }}
      required={!!required}
      error={showRequiredError}
      disabled={!!nonEditable}
      type={type}
      multiline={multiline}
      minRows={minRows}
      placeholder={placeholder}
      size={size}
      fullWidth={fullWidth}
      helperText={resolvedHelperText}
      InputLabelProps={{
        ...InputLabelProps,
        // Keep the label above an example/placeholder before the user focuses
        // the input, so the two never overlap.
        shrink: placeholder ? true : InputLabelProps?.shrink,
      }}
      inputProps={inputProps}
      InputProps={viewOnly ? { readOnly: true } : undefined}
    />
  );

  return (
    <Box sx={sx}>
      <BadgeSlot badge={badge} />
      {nonEditable && nonEditableReason ? (
        <Tooltip title={nonEditableReason}>
          <span>{field}</span>
        </Tooltip>
      ) : (
        field
      )}
    </Box>
  );
}

export interface ReadOnlyDerivedFieldProps {
  label: string;
  value: string;
  /** Reason explaining why the derived value cannot be edited here. */
  nonEditableReason: string;
  badge?: FieldBadgeKind;
  inputProps?: TextFieldProps['inputProps'];
  sx?: SxProps<Theme>;
}

/**
 * A thin convenience wrapper over `ComplexField` for intrinsically derived /
 * computed fields (e.g. Compliance's SAT Threshold, Record's computed Contract
 * Status / Option Period). Forces `nonEditable`.
 */
export function ReadOnlyDerivedField(props: ReadOnlyDerivedFieldProps): React.ReactElement {
  const { label, value, nonEditableReason, badge, inputProps, sx } = props;
  return (
    <ComplexField
      label={label}
      value={value}
      nonEditable
      nonEditableReason={nonEditableReason}
      badge={badge}
      inputProps={inputProps}
      sx={sx}
    />
  );
}
